import { Button, Dialog, Notice } from '@concord/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';

interface Row {
  id: string;
  publicId: number;
  firstName: string;
  lastName: string;
  email: string;
  planName: string | null;
  registrationState: string;
}

/**
 * INT-12, IAM-13: a case that arrives by telephone. Pick the patient, then complete the
 * same intake on their behalf; the case records both.
 */
export function NewCaseDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const results = useQuery({
    queryKey: ['patient-pick', q],
    queryFn: () => api<{ rows: Row[] }>(`/staff/directory?role=patient&q=${encodeURIComponent(q)}`),
    enabled: open && q.trim().length >= 2,
  });
  const start = useMutation({
    mutationFn: (patientId: string) => api<{ id: string }>('/cases', { body: { patientId } }),
    onSuccess: (r) => {
      onOpenChange(false);
      navigate(`/console/intake/${r.id}/type`);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New case by phone"
      description="Find the patient. You then take them through the same intake, on their behalf; the case records you both."
    >
      <input
        className="input"
        aria-label="Find the patient"
        placeholder="Name, email, phone or ID"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      {start.isError && <Notice tone="bad">{errorMessage(start.error)}</Notice>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
        {results.data?.rows.map((r) => (
          <div
            key={r.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '.8rem',
              borderBottom: '1px solid var(--rule)',
              padding: '.45rem 0',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pt">
                {r.firstName} {r.lastName} <span className="mono muted">ID {r.publicId}</span>
              </div>
              <div className="sub2">
                {r.email} · {r.planName ?? 'No plan'}
                {r.registrationState === 'invited' ? ' · not yet registered' : ''}
              </div>
            </div>
            <Button
              variant="sec"
              busy={start.isPending && start.variables === r.id}
              onClick={() => start.mutate(r.id)}
            >
              Start case
            </Button>
          </div>
        ))}
        {results.data?.rows.length === 0 && (
          <span className="sub2">No patient matches. Invite them from the directory first.</span>
        )}
      </div>
    </Dialog>
  );
}
